/**
 * オーバーレイ層（EP-2）。誌面の上に重ねる DOM で、エンジン非依存。
 *
 * 位置は「ページの画面矩形（engine.pageRect）× オーバーレイの 0–1 正規化矩形」で決まる。
 * 検索ハイライト（`search/highlight-layer.ts`）と同じ幾何を使うので、2D/3D・PDF/画像の
 * どの組み合わせでも同じ座標に出る。
 *
 * **要素はオーバーレイ 1 件につき 1 度だけ作り、以後は位置だけを更新する**（作り直さない）。
 * iframe は DOM の再親付けで中身がリロードされる仕様のため、M7b の動画・フォームが
 * 「めくっても再生が途切れない」ためにはこの持ち方が前提になる。同じ理由で層はエンジンの
 * ページツリーの外（host 直下）に常設する。
 *
 * **めくり中は層ごと伏せる**（3D の撓みに DOM を追随させられないため）。静止したら
 * 現在の幾何で配置し直す。
 *
 * ポインタ: 層自体は `pointer-events: none` で、個々のオーバーレイだけが受け取る。
 * さらに `pointerdown` を止める — host は pointerdown でポインタキャプチャを取るので、
 * 止めないとオーバーレイ上のクリックがスワイプ扱いになり click が届かない。
 */
import { cls } from '../brand';
import type { DisposeRegistry } from '../core/dispose-registry';
import type { ScreenRect } from '../engine/layout';
import type { OverlayContext, OverlayHandle, OverlayItem, OverlayRegistry } from './registry';
import type { OverlaysByPage } from './overlay-store';

/** ページ番号 → そのページの画面矩形（表示外は null）。 */
export type RectResolver = (pageNo: number) => ScreenRect | null;

interface Mounted {
  el: HTMLElement;
  handle: OverlayHandle;
  /** 直近に render で見えていたか（可視状態の変化だけを renderer に伝えるため）。 */
  visible: boolean;
}

export class OverlayLayer {
  private readonly el: HTMLDivElement;
  private readonly mounted = new Map<number, Mounted>();
  /** 要素 → 定義（クリックの逆引き用）。要素は捨てないので弱参照で足りる。 */
  private readonly byElement = new WeakMap<HTMLElement, OverlayItem>();
  private byPage: OverlaysByPage = new Map();

  constructor(
    host: HTMLElement,
    registry: DisposeRegistry,
    private readonly overlays: OverlayRegistry,
    private readonly context: OverlayContext,
    /**
     * オーバーレイが押されたときの通知（EP-6 の計測点・省略可）。層のルートに張った
     * 1 本の委譲リスナで拾うので、**あとから登録された renderer の要素も自動的に対象**になる
     * （renderer 側に計測を書かせない）。クロスオリジン iframe の内側は原理的に観測できない。
     */
    onItemClick?: (item: OverlayItem) => void,
  ) {
    this.el = context.doc.createElement('div');
    this.el.className = cls('overlays');
    host.appendChild(this.el);
    if (onItemClick) {
      registry.listen(this.el, 'click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const el = target.closest('.' + cls('overlay'));
        const item = el instanceof HTMLElement ? this.byElement.get(el) : undefined;
        if (item) onItemClick(item);
      });
    }
    registry.add('other', () => this.clear());
  }

  /** 定義を取り込む。以後 render で表示される。 */
  setOverlays(byPage: OverlaysByPage): void {
    this.byPage = byPage;
  }

  /** 描くものがあるか（render 要否の判定に使う）。 */
  get active(): boolean {
    return this.byPage.size > 0;
  }

  /**
   * 現在の表示に合わせて配置し直す。表示中ページのオーバーレイだけを見せる。
   * 表示外になった要素は隠すだけで捨てない（再訪時に作り直さないため）。
   */
  render(rectOf: RectResolver): void {
    const shown = new Set<number>();
    for (const [pageNo, items] of this.byPage) {
      const pageRect = rectOf(pageNo);
      if (!pageRect) continue;
      for (const item of items) {
        const entry = this.ensure(item);
        if (!entry) continue;
        shown.add(item.id);
        this.setVisible(item.id, entry, true);
        entry.el.style.left = `${pageRect.x + item.x * pageRect.width}px`;
        entry.el.style.top = `${pageRect.y + item.y * pageRect.height}px`;
        entry.el.style.width = `${item.w * pageRect.width}px`;
        entry.el.style.height = `${item.h * pageRect.height}px`;
      }
    }
    for (const [id, entry] of this.mounted) {
      if (!shown.has(id)) this.setVisible(id, entry, false);
    }
    this.el.hidden = false;
  }

  /**
   * 要素の可視状態を更新し、**変化したときだけ** renderer へ通知する。
   * 表示中は `hidden=false`、外れたら `hidden=true`。要素は捨てない。
   */
  private setVisible(id: number, entry: Mounted, visible: boolean): void {
    entry.el.hidden = !visible;
    if (entry.visible === visible) return;
    entry.visible = visible;
    entry.handle.visibilityChanged?.(visible);
  }

  /** 表示だけ消す（要素は保持・めくり中の退避に使う）。 */
  hide(): void {
    this.el.hidden = true;
  }

  /** すべて破棄する（renderer の後始末を含む）。 */
  clear(): void {
    for (const entry of this.mounted.values()) {
      entry.handle.dispose?.();
      entry.el.remove();
    }
    this.mounted.clear();
    this.byPage = new Map();
    this.el.remove();
  }

  /** 要素を 1 度だけ作る。描き手のない type は作らない（前方互換）。 */
  private ensure(item: OverlayItem): Mounted | null {
    const existing = this.mounted.get(item.id);
    if (existing) return existing;

    const renderer = this.overlays.rendererFor(item.type);
    if (!renderer) return null;

    const el = this.context.doc.createElement('div');
    el.className = `${cls('overlay')} ${cls(`overlay--${item.type}`)}`;
    el.style.zIndex = String(item.z);
    // host のポインタキャプチャ（スワイプ・パン）にクリックを奪わせない
    el.addEventListener('pointerdown', (event) => event.stopPropagation());

    // 戻り値は「dispose 関数」か「ハンドル」か undefined。ハンドルへ正規化する。
    const returned = renderer(el, item, this.context);
    const handle: OverlayHandle = typeof returned === 'function' ? { dispose: returned } : returned ?? {};
    this.el.appendChild(el);
    const entry: Mounted = { el, handle, visible: false };
    this.mounted.set(item.id, entry);
    this.byElement.set(el, item);
    return entry;
  }
}
